/* ============================================
   THE FINE SOURCE — Theme JavaScript
   ============================================ */

(function () {
  'use strict';

  /* ---- Utilities ---- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const U = (window.TFS && window.TFS.uiLocale) || {};
  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'GBP';
    const locale = window.Shopify?.locale || 'en-GB';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  };

  /* ---- Intersection observer for animations ---- */
  const animObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          animObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1 }
  );
  $$('.fade-in-up, .fade-in').forEach((el) => animObserver.observe(el));

  /* ---- Header: Mobile menu ---- */
  const hamburger = $('.hamburger');
  const mobileMenu = $('.mobile-menu');
  if (hamburger && mobileMenu) {
    hamburger.addEventListener('click', () => {
      const open = mobileMenu.classList.toggle('is-open');
      hamburger.classList.toggle('is-active', open);
      hamburger.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
    });
  }

  /* ---- Header: Language selector ---- */
  const langTrigger = $('.lang-trigger');
  const langDropdown = $('.lang-dropdown');
  if (langTrigger && langDropdown) {
    langTrigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = langDropdown.classList.toggle('is-open');
      langTrigger.setAttribute('aria-expanded', String(open));
    });
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.lang-selector')) {
        langDropdown.classList.remove('is-open');
        langTrigger.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /* ---- Header: Shop dropdown ---- */
  const shopDropdownWrapper = $('.shop-dropdown-wrapper');
  const shopDropdown = $('.shop-dropdown');
  const dropdownBackdrop = $('.dropdown-backdrop');

  if (shopDropdownWrapper && shopDropdown) {
    let hoverTimer;
    const openDropdown = () => {
      clearTimeout(hoverTimer);
      shopDropdown.classList.add('is-open');
      if (dropdownBackdrop) dropdownBackdrop.classList.add('is-open');
    };
    const closeDropdown = () => {
      hoverTimer = setTimeout(() => {
        shopDropdown.classList.remove('is-open');
        if (dropdownBackdrop) dropdownBackdrop.classList.remove('is-open');
      }, 100);
    };

    shopDropdownWrapper.addEventListener('mouseenter', openDropdown);
    shopDropdownWrapper.addEventListener('mouseleave', closeDropdown);
    shopDropdown.addEventListener('mouseenter', openDropdown);
    shopDropdown.addEventListener('mouseleave', closeDropdown);
    if (dropdownBackdrop) dropdownBackdrop.addEventListener('click', closeDropdown);
  }

  /* ---- Cart Drawer ---- */
  const cartDrawer = $('.cart-drawer');
  const cartOverlay = $('.cart-drawer-overlay');
  const cartTriggers = $$('[data-cart-trigger]');
  const cartClose = $('.cart-drawer-close');

  const openCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartDrawer.removeAttribute('aria-hidden');
    if (cartOverlay) cartOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    refreshCart();
  };
  const closeCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    if (cartOverlay) cartOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
  };

  cartTriggers.forEach((btn) => btn.addEventListener('click', openCart));
  if (cartClose) cartClose.addEventListener('click', closeCart);
  if (cartOverlay) cartOverlay.addEventListener('click', closeCart);

  /* ---- Cart: fetch and render ---- */
  const FREE_GIFT_THRESHOLD = window.TFS?.freeGiftThreshold ?? 5000;
  const FREE_SHIP_THRESHOLD = window.TFS?.freeShipThreshold ?? 10000;
  const L = window.TFS?.cartLocale || {};

  async function refreshCart() {
    try {
      const resp = await fetch('/cart.js');
      const cart = await resp.json();
      renderCartDrawer(cart);
      updateCartCount(cart.item_count);
    } catch (err) {
      console.error('Cart fetch error:', err);
    }
  }

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => (el.textContent = count));
  }

  function renderCartDrawer(cart) {
    const body = $('.cart-drawer-body');
    if (!body) return;

    const footer = $('.cart-drawer-footer');

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-empty">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 7H4l1-7z"/></svg>
          <p>${L.emptyTitle || 'Your bag is empty'}</p>
          <a href="${((window.TFS && window.TFS.rootUrl) || '/').replace(/\/$/, '')}/collections/all" style="font-size:0.75rem;color:var(--color-accent);text-decoration:underline;text-underline-offset:2px">${L.emptySubtitle || 'Discover our curated selection'}</a>
        </div>`;
      if (footer) { footer.style.display = 'none'; footer.setAttribute('aria-hidden', 'true'); }
      return;
    }

    const total = cart.total_price;
    const giftPct = Math.min((total / FREE_GIFT_THRESHOLD) * 100, 100);
    const shipPct = Math.min((total / FREE_SHIP_THRESHOLD) * 100, 100);
    const hasGift = total >= FREE_GIFT_THRESHOLD;
    const hasShip = total >= FREE_SHIP_THRESHOLD;
    const toGift = Math.max(FREE_GIFT_THRESHOLD - total, 0);
    const toShip = Math.max(FREE_SHIP_THRESHOLD - total, 0);

    const localeRoot = (window.TFS && window.TFS.rootUrl) || '/';
    const itemsHtml = cart.items.map((item) => {
      const hasDiscount = item.original_price > item.final_price;
      const compareHtml = hasDiscount
        ? `<span class="cart-item-compare">${formatMoney(item.original_price * item.quantity)}</span>`
        : '';
      const subBadge = item.selling_plan_allocation
        ? `<div class="cart-item-sub-badge">${L.subBadge || 'Member Savings 10% Off'}</div>`
        : '';
      const itemUrl = item.url.startsWith(localeRoot) ? item.url : (localeRoot.replace(/\/$/, '') + item.url);
      const itemImg = item.image ? item.image.replace(/(\.(jpg|jpeg|png|gif|webp))/, '_300x300$1') : '';
      return `
      <div class="cart-item" data-key="${item.key}">
        <a href="${itemUrl}" class="cart-item-img-link"><img class="cart-item-img" src="${itemImg}" alt="${item.title}" loading="lazy"></a>
        <div class="cart-item-info">
          <a href="${itemUrl}" class="cart-item-title-link"><div class="cart-item-title">${(window.TFS.productTitles && window.TFS.productTitles[item.product_id]) || item.product_title}</div></a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item-variant">${item.variant_title}</div>` : ''}
          <div class="cart-item-price">${compareHtml}${formatMoney(item.final_line_price)}</div>
          ${subBadge}
          <div class="cart-item-qty">
            <div class="qty-controls">
              <button class="qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}">−</button>
              <span class="qty-value">${item.quantity}</span>
              <button class="qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}">+</button>
            </div>
            <button class="cart-item-remove" data-key="${item.key}" aria-label="Remove item">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
            </button>
          </div>
        </div>
      </div>
    `; }).join('');

    body.innerHTML = `
      <div class="cart-progress">
        <div class="cart-progress-bar-track">
          <div class="cart-progress-bar-fill" style="width:${shipPct}%"></div>
          <div style="position:absolute;top:50%;transform:translateY(-50%);left:${(FREE_GIFT_THRESHOLD/FREE_SHIP_THRESHOLD)*100}%;width:1px;height:12px;background:rgba(26,25,23,0.3)"></div>
        </div>
        <div class="cart-progress-labels">
          <span class="${hasGift ? 'unlocked' : ''}">${hasGift ? (L.giftUnlocked || '🎁 Free gift unlocked!') : `${formatMoney(toGift)}${L.giftAway || ' away from a free gift'}`}</span>
          <span class="${hasShip ? 'unlocked' : ''}">${hasShip ? (L.shipUnlocked || '🚚 Free shipping!') : `${formatMoney(toShip)}${L.shipAway || ' away from free shipping'}`}</span>
        </div>
      </div>
      <div class="cart-items">${itemsHtml}</div>
      <div id="CartCompleteWith"></div>`;

    // Footer
    if (footer) {
      footer.style.display = '';
      footer.removeAttribute('aria-hidden');
      footer.innerHTML = `
        <div class="cart-total">
          <span class="cart-total-label">${L.total || 'Total'}</span>
          <span class="cart-total-amount">${formatMoney(cart.total_price)}</span>
        </div>
        <a href="/checkout" class="btn-checkout">${L.checkout || 'Proceed to Checkout'}</a>
        <p class="cart-note">${L.shippingNote || 'Shipping calculated at checkout'}</p>`;
    }

    // Qty handlers
    $$('.qty-btn', body).forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        const key = btn.dataset.key;
        const current = parseInt(btn.dataset.qty);
        const action = btn.dataset.action;
        const newQty = action === 'increase' ? current + 1 : Math.max(current - 1, 0);
        await updateCartItem(key, newQty);
      });
    });

    // Remove handlers
    $$('.cart-item-remove', body).forEach((btn) => {
      btn.addEventListener('click', async () => {
        await updateCartItem(btn.dataset.key, 0);
      });
    });

    // Complete With
    loadCompleteWith(cart);
  }

  async function loadCompleteWith(cart) {
    if (!cart.items.length) return;
    const container = document.getElementById('CartCompleteWith');
    if (!container) return;

    const lr = ((window.TFS && window.TFS.rootUrl) || '/').replace(/\/$/, '');
    const cartProductIds = new Set(cart.items.map((i) => i.product_id));
    const cartVendor = cart.items[0].vendor;
    const skipTags = new Set(['greece','italy','france','germany','bundle','gift']);
    const countryTags = new Set(['greece','italy','france','germany']);

    // Fetch first cart product's tags (not included in cart response)
    let cartTypeTags = [];
    let cartCountryTag = '';
    try {
      const prodResp = await fetch(`${lr}/products/${cart.items[0].handle}.js`);
      const prod = await prodResp.json();
      const tags = (prod.tags || []).map((t) => t.toLowerCase());
      cartTypeTags = tags.filter((t) => !skipTags.has(t));
      cartCountryTag = tags.find((t) => countryTags.has(t)) || '';
    } catch (e) {}

    // Fetch all available products not already in cart
    let pool = [];
    try {
      const resp = await fetch(`${lr}/collections/all/products.json?limit=250`);
      const data = await resp.json();
      pool = (data.products || []).filter(
        (p) => !cartProductIds.has(p.id) && p.variants?.some((v) => v.available)
      );
    } catch (e) {
      return;
    }

    if (!pool.length) return;

    // 3-pass priority — mirrors related-products.liquid
    const pass1 = pool.filter((p) => p.vendor === cartVendor);
    const pass1Ids = new Set(pass1.map((p) => p.id));

    const pass2 = pool.filter((p) => {
      if (pass1Ids.has(p.id)) return false;
      const pTags = (p.tags || []).map((t) => t.toLowerCase()).filter((t) => !skipTags.has(t));
      return pTags.some((t) => cartTypeTags.includes(t));
    });
    const pass2Ids = new Set(pass2.map((p) => p.id));

    const pass3 = !cartCountryTag ? [] : pool.filter((p) => {
      if (pass1Ids.has(p.id) || pass2Ids.has(p.id)) return false;
      return (p.tags || []).map((t) => t.toLowerCase()).includes(cartCountryTag);
    });

    const sorted = [...pass1, ...pass2, ...pass3].slice(0, 2);
    if (!sorted.length) return;

    container.innerHTML = `
      <div class="cwith-section">
        <p class="cwith-label">${L.completeWith || 'Complete With'}</p>
        ${sorted.map((p) => {
          /* Pick the first IN-STOCK variant for the cross-sell ATC. The product passed
             the pool filter (some variant available), but variants[0] may itself be
             sold out — adding a sold-out variant would 422 from Shopify and look broken. */
          const v = p.variants.find((vv) => vv.available) || p.variants[0];
          const imgSrc = p.images?.[0]?.src || '';
          return `
            <div class="cwith-item">
              <img class="cwith-img" src="${imgSrc}" alt="${(window.TFS.productTitles && window.TFS.productTitles[p.id]) || p.title}" loading="lazy">
              <div class="cwith-info">
                <a href="${lr}/products/${p.handle}" class="cwith-title">${(window.TFS.productTitles && window.TFS.productTitles[p.id]) || p.title}</a>
                <div class="cwith-price">${formatMoney(Math.round(parseFloat(v.price) * 100))}</div>
              </div>
              <button class="cwith-add-btn" data-variant-id="${v.id}">${L.add || 'Add'}</button>
            </div>`;
        }).join('')}
      </div>`;

    container.querySelectorAll('.cwith-add-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const vId = parseInt(btn.dataset.variantId, 10);
        btn.disabled = true;
        btn.textContent = '…';
        await addToCart(vId, 1);
      });
    });
  }

  async function updateCartItem(key, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      await refreshCart();
    } catch (err) {
      console.error('Cart update error:', err);
    }
  }

  async function addToCart(variantId, quantity = 1, sellingPlan = null) {
    const body = { id: variantId, quantity };
    if (sellingPlan) body.selling_plan = parseInt(sellingPlan, 10);
    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error('Failed to add item');
      await refreshCart();
      openCart();
    } catch (err) {
      console.error('Add to cart error:', err);
    }
  }

  // Expose globally
  window.TFS = window.TFS || {};
  window.TFS.addToCart = addToCart;
  window.TFS.openCart = openCart;
  window.TFS.refreshCart = refreshCart;

  /* ---- Product page: ATC form ---- */
  const atcForm = $('[data-product-form]');
  if (atcForm) {
    atcForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(atcForm);
      const variantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity') || '1');
      const sellingPlan = formData.get('selling_plan') || null;
      const btn = $('[data-atc-btn]', atcForm);
      if (btn) { btn.disabled = true; btn.textContent = U.adding || 'Adding\u2026'; }
      await addToCart(variantId, quantity, sellingPlan);
      if (btn) {
        btn.disabled = false;
        const subCb = document.querySelector('.pdp-sub-cb');
        btn.textContent = (subCb && subCb.checked) ? (U.subscribeSave || 'Subscribe & Save 10%') : (U.addToBag || 'Add to Bag');
      }
    });
  }

  /* ---- Product page: Variant selector ---- */
  const variantForms = $$('[data-variant-select]');
  variantForms.forEach((form) => {
    const buttons = $$('.variant-btn', form);
    const hiddenInput = $('[name="id"]', form.closest('[data-product-form]') || document);

    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        // Find variant radio or mark active
        const optionIndex = btn.dataset.optionIndex;
        const optionValue = btn.dataset.optionValue;

        // Mark active in its group
        $$(`[data-option-index="${optionIndex}"]`).forEach((b) =>
          b.classList.remove('active')
        );
        btn.classList.add('active');

        // Find matching variant
        const allSelected = $$('[data-option-index]')
          .filter((b) => b.classList.contains('active'))
          .map((b) => b.dataset.optionValue);

        const matchingVariant = findVariant(allSelected);
        if (matchingVariant && hiddenInput) {
          hiddenInput.value = matchingVariant.id;
          updateProductPrice(matchingVariant);
          updateVariantImages(matchingVariant);
        }
      });
    });
  });

  // On page load, show the currently selected variant's image.
  // Skip for single-variant products — the server-rendered image is already correct,
  // and the variantImageMap may point to a stale featured_image after Shopify admin edits.
  const initialVariantId = $('[data-variant-id-input]') && $('[data-variant-id-input]').value;
  if (initialVariantId && (window.TFS.productVariants || []).length > 1) {
    const initialVariant = (window.TFS.productVariants || []).find(
      (v) => String(v.id) === String(initialVariantId)
    );
    if (initialVariant) updateVariantImages(initialVariant);
  }

  function findVariant(selectedOptions) {
    const variantData = window.TFS.productVariants || [];
    return variantData.find((v) =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );
  }

  function updateProductPrice(variant) {
    const priceEl = $('[data-product-price]');
    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    const compareEl = $('[data-compare-price]');
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.textContent = '';
        compareEl.style.display = 'none';
      }
    }
    const atcBtn = $('[data-atc-btn]');
    if (atcBtn) {
      atcBtn.disabled = !variant.available;
      atcBtn.textContent = variant.available ? (U.addToBag || 'Add to Bag') : (U.soldOut || 'Sold Out');
    }
  }

  function updateVariantImages(variant) {
    if (!variant) return;
    const images = window.TFS.productImages || [];
    if (!images.length) return;

    // Use explicit featured_image mapping if available, otherwise fall back to variant's
    // position in the variants array (variant 0 → image 0, variant 1 → image 1, etc.)
    const imageMap = window.TFS.variantImageMap || {};
    const variants = window.TFS.productVariants || [];
    const variantIndex = variants.findIndex((v) => v.id === variant.id);

    let imgIndex = imageMap[String(variant.id)];
    if (imgIndex == null) {
      imgIndex = variantIndex >= 0 ? variantIndex : 0;
    }

    const src = images[imgIndex] || images[0];
    if (!src) return;

    // Build a matching srcset by swapping the size suffix in the Shopify image URL.
    // Without this, the original srcset wins and src changes have no visible effect.
    function makeSrcset(url) {
      const sizes = [400, 600, 800, 1200];
      return sizes
        .map(function (s) {
          return url.replace(/_\d+x(\.[a-z0-9]+)/i, '_' + s + 'x$1') + ' ' + s + 'w';
        })
        .join(', ');
    }
    const newSrcset = makeSrcset(src);

    // Update every slide-0 copy (original + 2 clones added by initPdpCarousel)
    document.querySelectorAll('.pdp-carousel-slide[data-image-index="0"] img').forEach(function (img) {
      img.src = src;
      img.srcset = newSrcset;
    });

    const firstStackedImg = document.querySelector('.pdp-stacked-slide img');
    if (firstStackedImg) {
      firstStackedImg.src = src;
      firstStackedImg.srcset = newSrcset;
    }

    if (window.TFS.pdpGoTo) window.TFS.pdpGoTo(0);
  }

  /* ---- Product page: Gallery ---- */
  const mainImg = $('.product-gallery-main img');
  const thumbs = $$('.product-gallery-thumb');
  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const src = thumb.dataset.src;
      const alt = thumb.dataset.alt;
      if (mainImg && src) { mainImg.src = src; mainImg.alt = alt || ''; }
      thumbs.forEach((t) => t.classList.remove('active'));
      thumb.classList.add('active');
    });
  });

  /* ---- Product page: Quantity buttons ---- */
  const qtyInc = $('[data-qty-inc]');
  const qtyDec = $('[data-qty-dec]');
  const qtyInput = $('[data-qty-input]');
  if (qtyInc && qtyDec && qtyInput) {
    qtyInc.addEventListener('click', () => {
      qtyInput.value = parseInt(qtyInput.value) + 1;
    });
    qtyDec.addEventListener('click', () => {
      const v = parseInt(qtyInput.value);
      if (v > 1) qtyInput.value = v - 1;
    });
  }

  /* ---- Product page: Accordion ---- */
  $$('.accordion-trigger').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.accordion-item');
      const isOpen = item.classList.contains('open');
      $$('.accordion-item').forEach((i) => i.classList.remove('open'));
      if (!isOpen) item.classList.add('open');
    });
  });

  /* ---- Product page: Subscription checkbox ---- */
  const subCheckbox = document.querySelector('.pdp-sub-cb');
  if (subCheckbox) {
    const atcForm2 = document.querySelector('[data-product-form]');
    const subBox = subCheckbox.closest('.pdp-sub-box');
    const sellingPlanId = subCheckbox.dataset.sellingPlanId;

    subCheckbox.addEventListener('change', () => {
      if (!atcForm2) return;
      let spInput = atcForm2.querySelector('[name="selling_plan"]');

      const atcBtn = document.querySelector('[data-atc-btn]');
      const priceEl = document.querySelector('[data-product-price]');

      function getCurrentVariant() {
        const vidInput = atcForm2 ? atcForm2.querySelector('[data-variant-id-input]') : null;
        if (!vidInput) return null;
        return (window.TFS.productVariants || []).find(
          (v) => String(v.id) === String(vidInput.value)
        ) || null;
      }

      if (subCheckbox.checked) {
        // Add selling_plan hidden input
        if (!spInput) {
          spInput = document.createElement('input');
          spInput.type = 'hidden';
          spInput.name = 'selling_plan';
          if (atcForm2) atcForm2.appendChild(spInput);
        }
        if (sellingPlanId) spInput.value = sellingPlanId;
        if (subBox) subBox.classList.add('is-active');

        // Show subscription price (10% off) and update button
        const baseVariant = getCurrentVariant();
        if (baseVariant && priceEl) {
          const subPrice = Math.round(baseVariant.price * 0.9);
          priceEl.textContent = formatMoney(subPrice);
          priceEl.dataset.subscriptionActive = '1';
        }
        if (atcBtn && !atcBtn.disabled) atcBtn.textContent = U.subscribeSave || 'Subscribe & Save 10%';
      } else {
        // Remove selling_plan input and restore original price + button
        if (spInput) spInput.remove();
        if (subBox) subBox.classList.remove('is-active');

        const baseVariant = getCurrentVariant();
        if (baseVariant && priceEl && priceEl.dataset.subscriptionActive) {
          priceEl.textContent = formatMoney(baseVariant.price);
          delete priceEl.dataset.subscriptionActive;
        }
        if (atcBtn && !atcBtn.disabled) atcBtn.textContent = U.addToBag || 'Add to Bag';
      }
    });
  }

  /* ---- Philosophy: Producer carousel ---- */
  const producerData = window.TFS && window.TFS.producers ? window.TFS.producers : [];
  let currentProducer = 0;

  const producerImg = $('.producer-img');
  const producerName = $('.producer-overlay-name');
  const producerSpecialty = $('.producer-overlay-specialty');
  const producerLocation = $('.producer-overlay-location');
  const producerDesc = $('.producer-description');
  const producerLink = $('.producer-discover');
  const producerDots = $$('.producer-dot');

  function showProducer(index) {
    if (!producerData.length) return;
    const p = producerData[index];
    if (producerImg) { producerImg.src = p.image; producerImg.alt = p.name; }
    if (producerName) producerName.textContent = p.name;
    if (producerSpecialty) producerSpecialty.textContent = p.specialty;
    if (producerLocation) producerLocation.textContent = p.location;
    if (producerDesc) producerDesc.textContent = p.description;
    if (producerLink) { producerLink.href = p.productUrl; producerLink.textContent = (U.discover || 'Discover') + ' ' + p.name; }
    producerDots.forEach((dot, i) => dot.classList.toggle('active', i === index));
    currentProducer = index;
  }

  const prevBtn = $('.producer-nav-btn[data-action="prev"]');
  const nextBtn = $('.producer-nav-btn[data-action="next"]');
  if (prevBtn) prevBtn.addEventListener('click', () => showProducer((currentProducer - 1 + producerData.length) % producerData.length));
  if (nextBtn) nextBtn.addEventListener('click', () => showProducer((currentProducer + 1) % producerData.length));
  producerDots.forEach((dot, i) => dot.addEventListener('click', () => showProducer(i)));

  /* ---- Hotspots ---- */
  $$('.hotspot').forEach((hotspot) => {
    const dot = $('.hotspot-dot', hotspot);
    if (dot) {
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        const isActive = hotspot.classList.toggle('is-active');
        $$('.hotspot').forEach((h) => { if (h !== hotspot) h.classList.remove('is-active'); });
      });
    }
  });
  document.addEventListener('click', () => $$('.hotspot').forEach((h) => h.classList.remove('is-active')));

  /* ---- Collection: filter drawer + sort dropdown ---- */
  (function () {
    var wraps = $$('.product-filter-wrap');
    if (!wraps.length) return;

    var drawer       = $('[data-ct-drawer]');
    var backdrop     = $('[data-ct-drawer-backdrop]');
    var openBtn      = $('[data-ct-open-filter]');
    var closeBtn     = $('[data-ct-close-filter]');
    var clearBtn     = $('[data-ct-clear-filter]');
    var applyBtn     = $('[data-ct-apply-filter]');
    var drawerCount  = $('[data-ct-drawer-count]');
    var toolbarCount = $('[data-ct-count]');
    var filterBadge  = $('[data-ct-filter-badge]');
    var sortToggle   = $('[data-ct-sort-toggle]');
    var sortMenu     = $('[data-ct-sort-toggle] + .ct-sort-menu') || $('.ct-sort-menu');
    var sortLabel    = $('[data-ct-sort-label]');
    var checkboxes   = $$('[data-ct-filter]');

    var selected = { type: new Set(), origin: new Set() };

    function originMatch(wrap, origin) {
      // origin matches if the product has the country tag (lowercased on data-product-tags)
      var tags = (wrap.dataset.productTags || '').split(',').map(function (t) { return t.trim(); });
      return tags.indexOf(origin) !== -1;
    }

    function typeMatch(wrap, type) {
      // Multi-category: data-product-type is a space-separated list of categories
      // (e.g. "olive-oil honey gifting" for a multi-product bundle).
      var types = (wrap.dataset.productType || '').trim().split(/\s+/);
      return types.indexOf(type) !== -1;
    }

    function computeVisible(typeSet, originSet) {
      var n = 0;
      wraps.forEach(function (wrap) {
        var okType   = !typeSet.size   || Array.from(typeSet).some(function (t) { return typeMatch(wrap, t); });
        var okOrigin = !originSet.size || Array.from(originSet).some(function (o) { return originMatch(wrap, o); });
        if (okType && okOrigin) n++;
      });
      return n;
    }

    var i18n = (window.TFS && window.TFS.collectionI18n) || {
      productsTpl: '{n} products',
      showResultsTpl: 'Show {n} results'
    };

    function applyToGrid() {
      var visibleCount = 0;
      wraps.forEach(function (wrap) {
        var okType   = !selected.type.size   || Array.from(selected.type).some(function (t) { return typeMatch(wrap, t); });
        var okOrigin = !selected.origin.size || Array.from(selected.origin).some(function (o) { return originMatch(wrap, o); });
        var show = okType && okOrigin;
        wrap.style.display = show ? '' : 'none';
        if (show) visibleCount++;
      });
      if (toolbarCount) toolbarCount.textContent = i18n.productsTpl.replace('__N__', visibleCount);

      // Filter badge
      var totalSel = selected.type.size + selected.origin.size;
      if (filterBadge) {
        if (totalSel) { filterBadge.hidden = false; filterBadge.textContent = String(totalSel); }
        else          { filterBadge.hidden = true; }
      }

      // URL sync (lightweight: persist filters so the back-button + share work)
      var url = new URL(window.location.href);
      if (selected.type.size)   url.searchParams.set('type',   Array.from(selected.type).join(',')); else url.searchParams.delete('type');
      if (selected.origin.size) url.searchParams.set('origin', Array.from(selected.origin).join(',')); else url.searchParams.delete('origin');
      history.replaceState(null, '', url.toString());
    }

    function updateDrawerLiveCount() {
      // Live count reflects the selections currently checked in the drawer
      var tmpType = new Set(), tmpOrigin = new Set();
      checkboxes.forEach(function (cb) {
        if (cb.checked) (cb.dataset.ctFilter === 'type' ? tmpType : tmpOrigin).add(cb.value);
      });
      var n = computeVisible(tmpType, tmpOrigin);
      if (applyBtn) applyBtn.textContent = i18n.showResultsTpl.replace('__N__', n);
    }

    function openDrawer() {
      if (!drawer) return;
      drawer.classList.add('is-open');
      if (backdrop) { backdrop.hidden = false; backdrop.classList.add('is-open'); }
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      updateDrawerLiveCount();
    }
    function closeDrawer() {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      if (backdrop) { backdrop.classList.remove('is-open'); setTimeout(function () { backdrop.hidden = true; }, 250); }
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    if (openBtn)   openBtn.addEventListener('click', openDrawer);
    if (closeBtn)  closeBtn.addEventListener('click', closeDrawer);
    if (backdrop)  backdrop.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer && drawer.classList.contains('is-open')) closeDrawer();
    });

    checkboxes.forEach(function (cb) {
      cb.addEventListener('change', updateDrawerLiveCount);
    });

    if (clearBtn) clearBtn.addEventListener('click', function () {
      checkboxes.forEach(function (cb) { cb.checked = false; });
      updateDrawerLiveCount();
    });

    if (applyBtn) applyBtn.addEventListener('click', function () {
      selected.type.clear();
      selected.origin.clear();
      checkboxes.forEach(function (cb) {
        if (cb.checked) (cb.dataset.ctFilter === 'type' ? selected.type : selected.origin).add(cb.value);
      });
      applyToGrid();
      closeDrawer();
    });

    // Sort dropdown
    if (sortToggle && sortMenu) {
      sortToggle.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = !sortMenu.hidden;
        sortMenu.hidden = isOpen;
        sortToggle.setAttribute('aria-expanded', String(!isOpen));
      });
      document.addEventListener('click', function (e) {
        if (!sortMenu.hidden && !sortMenu.contains(e.target) && e.target !== sortToggle) {
          sortMenu.hidden = true;
          sortToggle.setAttribute('aria-expanded', 'false');
        }
      });
      // Reflect current sort label from active item
      var activeSort = sortMenu.querySelector('a.is-active');
      if (activeSort && sortLabel) sortLabel.textContent = activeSort.textContent.trim();

      // Preserve current filter URL params (?type=, ?origin=) when changing sort.
      // The default <a href> would drop them on navigation; we rebuild the URL.
      sortMenu.querySelectorAll('a[data-ct-sort-value]').forEach(function (link) {
        link.addEventListener('click', function (e) {
          e.preventDefault();
          var url = new URL(window.location.href);
          url.searchParams.set('sort_by', link.dataset.ctSortValue);
          // Re-build type / origin params from CURRENT selection (not URL —
          // since the user may have toggled filters this session without applying)
          if (selected.type.size)   url.searchParams.set('type',   Array.from(selected.type).join(',')); else url.searchParams.delete('type');
          if (selected.origin.size) url.searchParams.set('origin', Array.from(selected.origin).join(',')); else url.searchParams.delete('origin');
          window.location.href = url.toString();
        });
      });
    }

    // Init from URL (?type=caviar,honey&origin=italy)
    var params = new URLSearchParams(window.location.search);
    var initType   = (params.get('type')   || '').split(',').filter(Boolean);
    var initOrigin = (params.get('origin') || '').split(',').filter(Boolean);
    // Backwards-compat with the old ?filter=X param
    var legacyFilter = params.get('filter');
    if (legacyFilter && !initType.length && !initOrigin.length) {
      // Heuristic: if it matches a known origin, treat as origin; else treat as type
      if (['greece', 'italy', 'germany', 'france'].indexOf(legacyFilter) !== -1) initOrigin.push(legacyFilter);
      else if (legacyFilter !== 'all') initType.push(legacyFilter === 'bundle' ? 'gifting' : legacyFilter);
    }
    checkboxes.forEach(function (cb) {
      var bucket = cb.dataset.ctFilter === 'type' ? initType : initOrigin;
      if (bucket.indexOf(cb.value) !== -1) cb.checked = true;
    });
    if (initType.length || initOrigin.length) {
      initType.forEach(function (t) { selected.type.add(t); });
      initOrigin.forEach(function (o) { selected.origin.add(o); });
      applyToGrid();
    } else {
      // Set initial product count text
      if (toolbarCount) toolbarCount.textContent = i18n.productsTpl.replace('__N__', wraps.length);
    }
  })();

  /* ---- Newsletter form ---- */
  /* Handled by inline Klaviyo script in newsletter.liquid and popup-newsletter.liquid */

  /* ---- Init ---- */
  refreshCart();

  /* ---- Generic swipe-gesture helper for carousels ---- */
  window.TFS = window.TFS || {};
  window.TFS.attachSwipe = function (trackEl, onSwipe) {
    if (!trackEl || typeof onSwipe !== 'function') return;
    var sx = 0, sy = 0, tracking = false;
    trackEl.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { tracking = false; return; }
      sx = e.touches[0].clientX;
      sy = e.touches[0].clientY;
      tracking = true;
    }, { passive: true });
    trackEl.addEventListener('touchend', function (e) {
      if (!tracking) return;
      tracking = false;
      var t = e.changedTouches[0];
      var dx = t.clientX - sx;
      var dy = t.clientY - sy;
      if (Math.abs(dx) < 40) return;
      if (Math.abs(dx) < Math.abs(dy) * 1.5) return;
      onSwipe(dx < 0 ? 1 : -1);
    }, { passive: true });
    trackEl.addEventListener('touchcancel', function () { tracking = false; }, { passive: true });
  };

  /* ---- PDP: Image Carousel (mobile) — infinite loop ---- */
  function initPdpCarousel() {
    const track = document.getElementById('PdpCarouselTrack');
    if (!track) return;
    const origSlides = Array.from(track.querySelectorAll('.pdp-carousel-slide'));
    const n = origSlides.length;
    if (n < 2) return;
    const dots = document.querySelectorAll('.pdp-carousel-dot');

    // Clone: prepend + append full set for seamless loop
    origSlides.forEach(s => track.appendChild(s.cloneNode(true)));
    for (let j = n - 1; j >= 0; j--) track.insertBefore(origSlides[j].cloneNode(true), track.firstChild);

    let idx = n;
    let busy = false;

    function jump(i, anim) {
      track.style.transition = anim ? 'transform 0.4s cubic-bezier(0.4,0,0.2,1)' : 'none';
      track.style.transform = 'translateX(-' + (i * 100) + '%)';
      dots.forEach((d, di) => d.classList.toggle('active', di === ((i - n + n) % n)));
    }

    jump(idx, false);

    function go(dir) {
      if (busy) return;
      busy = true;
      idx += dir;
      jump(idx, true);
    }

    track.addEventListener('transitionend', () => {
      if (idx >= 2 * n) { idx -= n; jump(idx, false); }
      else if (idx < n) { idx += n; jump(idx, false); }
      busy = false;
    });

    const prev = document.querySelector('.pdp-carousel-prev');
    const next = document.querySelector('.pdp-carousel-next');
    if (prev) prev.addEventListener('click', () => go(-1));
    if (next) next.addEventListener('click', () => go(1));
    dots.forEach((d, i) => d.addEventListener('click', () => {
      if (busy) return;
      busy = true;
      idx = n + i;
      jump(idx, true);
    }));

    // Expose for variant image switching
    window.TFS.pdpGoTo = function(i) { idx = n + i; jump(idx, true); };

    // Swipe-gesture support
    window.TFS.attachSwipe(track, go);
  }
  initPdpCarousel();

  /* ---- PDP: Story / Sourcing Tabs ---- */
  function initPdpTabs() {
    const tabs = $$('.pdp-tab');
    if (!tabs.length) return;
    tabs.forEach(function(tab) {
      tab.addEventListener('click', function() {
        const target = tab.dataset.tab;
        tabs.forEach(function(t) {
          t.classList.toggle('is-active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });
        $$('.pdp-tab-panel').forEach(function(panel) {
          panel.classList.toggle('is-active', panel.id === 'pdp-tab-' + target);
        });
      });
    });
  }
  initPdpTabs();

})();
